import { apiV1Service } from '@/utils/request'
import { USER_ENDPOINTS } from './endpoints'
import type { ApiResponse } from '@/manage/types/common'
import { isApiResponse, unwrapApiData, unwrapNullableApiData } from './response'
import { envConfig } from '@/shared/config/env'
import { requestAuthenticatedStream } from '@/utils/streamRequest'
import { withRequestTimeout } from '@/manage/utils/requestGuard'
import type {
  UserBusinessProfile,
  LinkedInAccountType,
  UserTag,
  UserAsset,
  PointSubject,
  UserPointsAction,
  UserPointsItem,
  UserPointsQuery,
  UserPointsResponse,
  UserPointsType,
  UserJobListResp,
  UserTagListResp,
  UserJobListPublic,
  UserJobPublic,
  UserJobCreate,
  UserJobPatch,
  UpdateUserBusinessProfilePayload,
} from '@/manage/types/user'
import { usePostHog } from '@/shared/composables/usePostHog'
import { useUserStore } from '@/manage/stores/user'

const { posthog } = usePostHog()

type NullableUserBusinessProfile = {
  [K in keyof UserBusinessProfile]?: UserBusinessProfile[K] | null
}

type UserBusinessProfileResponse =
  | ApiResponse<NullableUserBusinessProfile | null>
  | { data: NullableUserBusinessProfile | null }
  | NullableUserBusinessProfile
  | null

const unwrapUserBusinessProfile = (
  payload: UserBusinessProfileResponse,
): NullableUserBusinessProfile | null => {
  if (isApiResponse<NullableUserBusinessProfile | null>(payload)) {
    return payload.data ?? null
  }

  if (
    payload &&
    typeof payload === 'object' &&
    !Array.isArray(payload) &&
    Object.keys(payload).length === 1 &&
    Object.prototype.hasOwnProperty.call(payload, 'data')
  ) {
    return (payload as { data: NullableUserBusinessProfile | null }).data ?? null
  }

  return (payload as NullableUserBusinessProfile | null) ?? null
}

export type {
  ApiResponse,
  UserBusinessProfile,
  LinkedInAccountType,
  UserTag,
  UserAsset,
  PointSubject,
  UserPointsAction,
  UserPointsItem,
  UserPointsQuery,
  UserPointsResponse,
  UserPointsType,
  UserJobListResp,
  UserJobListPublic,
  UserJobPublic,
  UserJobCreate,
  UserJobPatch,
  UpdateUserBusinessProfilePayload,
}

export type UserCandidateLinkState = 'Added' | 'NotSent' | 'Pending'
export type UserJobAction = 'open' | 'close' | 'confidential' | 'public'

export interface PatchUserCandidateLinkItem {
  platform_id?: string | null
  candidate_id?: string | null
  state: UserCandidateLinkState
  send_at?: string | null
}

export interface PatchUserCandidateLinkReqs {
  items: PatchUserCandidateLinkItem[]
  email: string
  name: string
}

type GreetingPlatform =
  | 'LinkedIn'
  | 'WeChat'
  | 'GitHub'
  | 'Twitter'
  | 'Facebook'
  | 'Instagram'
  | 'TikTok'

const GREETING_PLATFORM_MAP: Record<string, GreetingPlatform> = {
  linkedin: 'LinkedIn',
  wechat: 'WeChat',
  github: 'GitHub',
  twitter: 'Twitter',
  facebook: 'Facebook',
  instagram: 'Instagram',
  tiktok: 'TikTok',
}

const normalizeGreetingPlatform = (platform: string) => {
  const normalized = String(platform || '')
    .trim()
    .toLowerCase()
  return GREETING_PLATFORM_MAP[normalized] || platform
}

type UserJobStreamEvent = {
  event?: string
  data?: unknown
}

type UserJobStreamResult = {
  job_id?: string
  id?: string
  name?: string
  jd?: string
  data?: UserJobPublic['data']
  user_patch?: UserJobPublic['user_patch']
  status?: UserJobPublic['status']
  open_at?: UserJobPublic['open_at']
  close_at?: UserJobPublic['close_at']
  job_arc_id?: UserJobPublic['job_arc_id']
  uname?: UserJobPublic['uname']
}

const inflightUserJobsRequests = new Map<string, Promise<UserJobListResp>>()

const parseSseEventBlock = (block: string): UserJobStreamEvent | null => {
  const lines = block
    .split('\n')
    .map((line) => line.replace(/\r$/, ''))
    .filter(Boolean)

  if (!lines.length) return null

  let event = ''
  const dataLines: string[] = []

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice(6).trim()
      continue
    }
    if (line.startsWith('data:')) {
      dataLines.push(line.slice(5).trim())
    }
  }

  if (!event && !dataLines.length) return null

  let data: unknown = dataLines.join('\n')
  try {
    data = JSON.parse(String(data))
  } catch (error) {
    console.error('[User API] SSE 数据 JSON 解析失败:', error)
    posthog.capture('api_parse_error', {
      module: 'user',
      context: 'sse_json_parse',
      error: String(error),
    })
    data = dataLines.join('\n')
  }

  return { event, data }
}

const parseSseChunks = (buffer: string) => {
  const normalized = buffer.replace(/\r\n/g, '\n')
  const segments = normalized.split('\n\n')
  const completed = segments.slice(0, -1)
  const remainder = segments[segments.length - 1] || ''
  const events: UserJobStreamEvent[] = []

  for (const block of completed) {
    const parsed = parseSseEventBlock(block)
    if (parsed) events.push(parsed)
  }

  return { events, remainder }
}

const mapStreamResultToUserJob = (payload: UserJobStreamResult): UserJobPublic => ({
  id: String(payload.id || payload.job_id || ''),
  name: String(payload.name || ''),
  jd: String(payload.jd || ''),
  data:
    payload.data ||
    ({
      age_range: '',
      domain: '',
      evaluation_framework: {
        role_summary: '',
        must_have_criteria: [],
        preferred_criteria: [],
        inferred_criteria: [],
        scoring_focus: [],
      },
      function_root: '',
      job_titles: [],
      keywords: [],
      location: '',
      primary_industry: '',
      secondary_industry: [],
      seniority_level: '',
      weights: {
        age: 0,
        domain: 0,
        industry: 0,
        job_title: 0,
        language: 0,
        location: 0,
        years_of_experience: 0,
      },
      years_of_experience: '',
    } satisfies UserJobPublic['data']),
  user_patch: payload.user_patch || {},
  job_arc_id: payload.job_arc_id ?? null,
  open_at: payload.open_at ?? null,
  close_at: payload.close_at ?? null,
  uname: payload.uname,
  status: payload.status || 'PENDING',
})

const consumeUserJobStreamResponse = async (
  response: Response,
  context: { markStreamingStarted: () => void },
  options?: { requireResult?: boolean },
) => {
  if (response.status === 204) return null

  const contentType = response.headers.get('content-type') || ''

  if (!contentType.includes('text/event-stream') || !response.body) {
    const text = (await response.text()).trim()
    if (!text) return null
    const payload = JSON.parse(text) as ApiResponse<UserJobPublic | null> | UserJobPublic | null
    return unwrapNullableApiData<UserJobPublic>(payload)
  }

  const reader = response.body.getReader()
  const decoder = new TextDecoder()
  let buffer = ''
  let latestResult: UserJobPublic | null = null

  while (true) {
    const { done, value } = await reader.read()
    if (done) break
    context.markStreamingStarted()
    buffer += decoder.decode(value, { stream: true })
    const { events, remainder } = parseSseChunks(buffer)
    buffer = remainder

    for (const event of events) {
      if (event.event !== 'result') continue
      if (event.data && typeof event.data === 'object') {
        latestResult = mapStreamResultToUserJob(event.data as UserJobStreamResult)
      }
    }
  }

  buffer += decoder.decode()
  const tailEvent = parseSseEventBlock(buffer.trim())
  if (tailEvent?.event === 'result' && tailEvent.data && typeof tailEvent.data === 'object') {
    latestResult = mapStreamResultToUserJob(tailEvent.data as UserJobStreamResult)
  }

  if (!latestResult && options?.requireResult) {
    throw new Error('User job stream returned no result event')
  }

  return latestResult
}

export const userApi = {
  getUserProfile() {
    return apiV1Service
      .get<UserBusinessProfileResponse>(USER_ENDPOINTS.profile)
      .then(unwrapUserBusinessProfile)
  },

  updateUserProfile(data: UpdateUserBusinessProfilePayload) {
    return apiV1Service
      .put<UserBusinessProfileResponse>(USER_ENDPOINTS.profile, data)
      .then(unwrapUserBusinessProfile)
  },

  getUserTags(params?: { admin?: boolean; limit?: number; page?: number; query?: string | null }) {
    return apiV1Service
      .get<ApiResponse<UserTagListResp> | UserTagListResp>(USER_ENDPOINTS.tags, { params })
      .then((resp) => unwrapApiData<UserTagListResp>(resp))
  },

  getUserAsset() {
    return apiV1Service
      .get<ApiResponse<UserAsset> | UserAsset>(USER_ENDPOINTS.asset)
      .then((resp) => unwrapApiData<UserAsset>(resp))
  },

  getUserPoints(params?: UserPointsQuery) {
    return apiV1Service
      .get<ApiResponse<UserPointsResponse> | UserPointsResponse>(USER_ENDPOINTS.points, {
        params,
      })
      .then((resp) => unwrapApiData<UserPointsResponse>(resp))
  },

  createUserTag(data: { name: string }) {
    return apiV1Service
      .post<ApiResponse<UserTag> | UserTag>(USER_ENDPOINTS.tags, data)
      .then((resp) => unwrapApiData<UserTag>(resp))
  },

  updateUserTag(tagId: string, data: { name?: string; is_top?: boolean }) {
    return apiV1Service
      .patch<ApiResponse<UserTag> | UserTag>(USER_ENDPOINTS.tag(tagId), data)
      .then((resp) => unwrapApiData<UserTag>(resp))
  },

  deleteUserTag(tagId: string) {
    return apiV1Service
      .delete<ApiResponse<null> | null>(USER_ENDPOINTS.tag(tagId))
      .then((resp) => unwrapNullableApiData<null>(resp))
  },

  getGreetings(platform: string, type: string) {
    return apiV1Service
      .get<
        ApiResponse<Record<string, string>> | Record<string, string>
      >(USER_ENDPOINTS.greeting(normalizeGreetingPlatform(platform), type))
      .then((resp) => unwrapApiData<Record<string, string>>(resp))
  },

  saveGreeting(platform: string, type: string, data: Record<string, string>) {
    return apiV1Service
      .patch<
        ApiResponse<Record<string, string>>
      >(USER_ENDPOINTS.greeting(normalizeGreetingPlatform(platform), type), data)
      .then((resp) => unwrapApiData<Record<string, string>>(resp))
  },

  deleteGreeting(platform: string, type: string, keys: string[]) {
    return apiV1Service
      .delete<ApiResponse<null>>(
        USER_ENDPOINTS.greeting(normalizeGreetingPlatform(platform), type),
        {
          data: { keys },
        },
      )
      .then((resp) => unwrapApiData<null>(resp))
  },

  patchUserCandidateLinks(platform: 'linkedin', data: PatchUserCandidateLinkReqs) {
    return apiV1Service
      .patch<ApiResponse<void>>(USER_ENDPOINTS.candidateLink(platform), data, {
        __silenceGlobalError: true,
      })
      .then((resp) => unwrapApiData<void>(resp))
  },

  getUserJobs(params?: { status?: 'draft' | 'open' | 'close' | null }) {
    const user = useUserStore()
    const key = JSON.stringify([user.user?.sub, user.isAdminModeActive, params || {}])
    const inflight = inflightUserJobsRequests.get(key)
    if (inflight) return inflight

    const request = apiV1Service
      .get<ApiResponse<UserJobListResp> | UserJobListResp>(USER_ENDPOINTS.jobs, { params })
      .then((resp) => unwrapApiData<UserJobListResp>(resp))
      .finally(() => {
        inflightUserJobsRequests.delete(key)
      })

    inflightUserJobsRequests.set(key, request)
    return request
  },

  createUserJob(data: UserJobCreate) {
    return requestAuthenticatedStream<UserJobCreate, UserJobPublic | null>({
      baseURL: envConfig.apiV1BaseUrl,
      endpoint: USER_ENDPOINTS.jobs,
      body: data,
      authReason: 'user_job_create',
      consumeResponse: (response, context) =>
        consumeUserJobStreamResponse(response, context, { requireResult: true }),
    })
  },

  getUserJob(jobId: string) {
    return apiV1Service
      .get<ApiResponse<UserJobPublic | null> | UserJobPublic | null>(USER_ENDPOINTS.job(jobId))
      .then((resp) => unwrapNullableApiData<UserJobPublic>(resp))
  },

  updateUserJob(jobId: string, data: UserJobPatch) {
    return requestAuthenticatedStream<UserJobPatch, UserJobPublic | null>({
      baseURL: envConfig.apiV1BaseUrl,
      endpoint: USER_ENDPOINTS.job(jobId),
      body: data,
      authReason: 'user_job_update',
      consumeResponse: (response, context) => consumeUserJobStreamResponse(response, context),
    })
  },

  renameUserJob(jobId: string, data: { action: UserJobAction; name: string }) {
    return apiV1Service
      .patch<ApiResponse<void> | void>(USER_ENDPOINTS.job(jobId), undefined, {
        params: data,
      })
      .then((resp) => unwrapApiData<void>(resp))
  },

  patchUserJobStatus(jobId: string, action: UserJobAction) {
    return withRequestTimeout(
      apiV1Service
        .patch<ApiResponse<void> | void>(USER_ENDPOINTS.job(jobId), undefined, {
          params: { action },
          timeout: 15_000,
        })
        .then((resp) => unwrapApiData<void>(resp)),
      15_000,
      `Patch user job status "${action}" timed out`,
    )
  },

  deleteUserJob(jobId: string) {
    return apiV1Service
      .delete<ApiResponse<void> | void>(USER_ENDPOINTS.job(jobId))
      .then((resp) => unwrapApiData<void>(resp))
  },
}
